/* @flow */

import * as React from 'react';
import { observable, action } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import Moment from 'react-moment';
import type { TranslatorProps } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import type { RouteComponentProps } from 'react-router-dom';

import Loading from 'components/Loading/Loading';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import NotificationStore from 'stores/NotificationStore';
import { setupManufacturerAddition } from 'views/NotificationsReview/forms/ManufacturerAdditionRequest/setupManufacturerAddition';
import ManufacturerAdditionRequest from 'views/NotificationsReview/forms/ManufacturerAdditionRequest/ManufacturerAdditionRequest';
import { setupProductAdditionRequest } from 'views/NotificationsReview/forms/ProductAdditionRequest/setupProductAdditionRequest';
import ProductAdditionRequest from 'views/NotificationsReview/forms/ProductAdditionRequest/ProductAdditionRequest';
import { setupRentalAssetExtension } from 'views/NotificationsReview/forms/RentalAssetExtensionRequest/setupRentalAssetExtension';
import RentalAssetExtensionRequest from 'views/NotificationsReview/forms/RentalAssetExtensionRequest/RentalAssetExtensionRequest';
import { getEventCategory, getNotificationCopy } from 'utils/NotificationUtils';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { browserHistory } from 'services/ServiceRegistry';

import ReviewFormControls from './components/ReviewFormControls/ReviewFormControls';
import ReviewData from './components/ReviewData/ReviewData';
import ReviewPanel from './panels/ReviewPanel/ReviewPanel';
import CurrentPanel from './panels/CurrentPanel/CurrentPanel';
import FuturePanel from './panels/FuturePanel/FuturePanel';
import DetailsPanel from './panels/DetailsPanel/DetailsPanel';

import styles from './NotificationsReview.module.scss';
import AssetCalibrationDue from './panels/ReviewPanel/AssetCalibrationDue';
import { variantIcon } from '../Notifications/NotificationPanel/NotificationPanel';

type InjectedProps = {
  notificationStore: NotificationStore,
} & TranslatorProps &
  RouteComponentProps;

@translate()
@inject('notificationStore')
@observer
export default class NotificationsReview extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  @observable localState = {
    inEditMode: false,
    notification: null,
    form: null,
    loading: false,
  };

  @action setMode = (value: boolean) => {
    this.localState.inEditMode = value;
  };

  componentDidMount() {
    this.loadNotification();
  }

  @action
  async loadNotification() {
    this.localState.loading = true;
    const { notificationStore, match } = this.props;
    const response = await notificationStore.getNotificationById(match.params.id);

    if (response) {
      this.setNotification(response);
    } else {
      // show 404
      browserHistory.push('/404', { error: 'Resource was not found' });
    }

    this.localState.loading = false;
  }

  setNotification = (obj: Object) => {
    this.localState = {
      ...this.localState,
      notification: obj,
      form: this.getForm(obj),
    };
  };

  getForm = (obj: Object) => {
    const { notificationStore, t } = this.props;

    switch (obj.event_type) {
      case 'ManufacturerAdditionRequest':
        return setupManufacturerAddition(
          obj.actions.find((act: Object) => act.type === 'approve'),
          obj,
          notificationStore.handleAction,
          this.onFormSubmit,
          t
        );
      case 'ProductAdditionRequest':
        return setupProductAdditionRequest(
          obj.actions.find((act: Object) => act.type === 'approve'),
          obj,
          notificationStore.handleAction,
          this.onFormSubmit,
          t
        );
      case 'ContractAssetsExtendRequest':
      case 'AssetRentalExtensionRequest':
        return setupRentalAssetExtension(
          obj.actions.find((act: Object) => act.type === 'approve'),
          obj,
          notificationStore.handleAction,
          this.onFormSubmit,
          t
        );
      default:
        return null;
    }
  };

  onFormSubmit = () => {
    this.props.history.push('/notifications', {
      search: `tab=1`,
      state: { tab: 1 },
    });
  };

  renderFuturePanel() {
    switch (this.localState.notification.event_type) {
      case 'ManufacturerAdditionRequest':
        return (
          <FuturePanel>
            <ManufacturerAdditionRequest
              form={this.localState.form}
              setMode={this.setMode}
              inEditMode={this.localState.inEditMode}
              itemData={this.localState.notification.actions.find((act: Object) => {
                return act.type === 'approve';
              })}
            />
          </FuturePanel>
        );
      case 'ProductAdditionRequest':
        return (
          <FuturePanel>
            <ProductAdditionRequest
              form={this.localState.form}
              setMode={this.setMode}
              inEditMode={this.localState.inEditMode}
              itemData={this.localState.notification.actions.find((act: Object) => {
                return act.type === 'approve';
              })}
            />
          </FuturePanel>
        );
      case 'ContractAssetsExtendRequest':
      case 'AssetRentalExtensionRequest':
        return (
          <FuturePanel>
            <RentalAssetExtensionRequest
              form={this.localState.form}
              setMode={this.setMode}
              inEditMode={this.localState.inEditMode}
            />
          </FuturePanel>
        );
      default:
        if (this.localState.notification.template_values.review.future) {
          return (
            <FuturePanel>
              <ReviewData
                data={this.localState.notification.template_values.review.future}
                isCurrent={false}
                t={this.props.t}
              />
            </FuturePanel>
          );
        }

        return null;
    }
  }

  renderDetailsPanel(values) {
    // TODO: Here all the sent requests must display Details Panel with asset info
    if (
      values.event_type === 'AssetRentalExtensionRequest' ||
      values.event_type === 'ContractAssetsExtendRequest'
    ) {
      return <DetailsPanel data={values} />;
    }
  }

  renderNotificationBody(eventType: string, data: Object) {
    switch (eventType) {
      case 'AssetCalibrationDue':
        return <AssetCalibrationDue data={data.contract} />;
    }
  }

  render() {
    const { t, notificationStore } = this.props;

    if (this.localState.notification && !this.localState.loading) {
      const values = {
        ...this.localState.notification,
        ...this.localState.notification.template_values,
      };

      const titleCopyInfo = getNotificationCopy(this.localState.notification, 'Title');
      const bodyCopyInfo = getNotificationCopy(this.localState.notification, 'Body');
      const eventCategory = getEventCategory(this.localState.notification);
      const icon = variantIcon[eventCategory];

      return (
        <PageWrapper isFlex={false}>
          {this.localState.notification && (
            <FormWrapper
              title={
                t('general:notifications.reviewof') +
                ' ' +
                t(titleCopyInfo.key, { ...values, ...titleCopyInfo.keys })
              }
              hoistChildren={'1'}
            >
              <ReviewPanel
                icon={icon}
                title={t(bodyCopyInfo.key, { ...values, ...bodyCopyInfo.options })}
                date={
                  <Moment
                    date={this.localState.notification.created_at}
                    format="MMMM DD, YYYY - hh:mm a"
                  />
                }
              />
              {this.localState.notification.template_values &&
                this.localState.notification.template_values.review && (
                  <Grid className={styles['notifications-grid']} container={true}>
                    {this.renderDetailsPanel(values)}
                    {this.localState.notification.template_values.review.current && (
                      <CurrentPanel>
                        <ReviewData
                          data={this.localState.notification.template_values.review.current}
                          template={this.localState.notification.template_values}
                          isCurrent={true}
                          t={this.props.t}
                        />
                      </CurrentPanel>
                    )}
                    {this.renderFuturePanel()}
                  </Grid>
                )}

              {this.renderNotificationBody(
                this.localState.notification.event_type,
                this.localState.notification
              )}

              {!this.props.notificationType && (
                <ReviewFormControls
                  notification={this.localState.notification}
                  inEditMode={this.localState.inEditMode}
                  form={this.localState.form}
                  handleAction={notificationStore.handleAction}
                  callback={this.onFormSubmit}
                />
              )}
            </FormWrapper>
          )}
        </PageWrapper>
      );
    }

    return (
      <PageWrapper>
        <Loading />
      </PageWrapper>
    );
  }
}
